import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import { RequirePortalAuth } from "components/portal-route";
import { lazy, Suspense } from "react";
import { Route, Routes } from "react-router-dom";
// Core components - keep these as regular imports for immediate availability
import PageNotFound from "./components/PageNotFound";
import { RequireAuth } from "./components/PrivateRoute";
import { canAccessCommitmentsModule } from "./services/commitments-access";
import { UserRoles } from "./types/enums";

// Lazy-loaded route components - Events Module
const EventList = lazy(
  () => import("./components/dashboards/events/event-list")
);
const EventForm = lazy(
  () => import("./components/dashboards/events/event-form")
);
const EventDetailsContainer = lazy(() =>
  import("./components/dashboards/events/event-details-container").then(
    (m) => ({
      default: m.EventDetailsContainer,
    })
  )
);

// Lazy-loaded route components - Assets Module
const AssetList = lazy(() =>
  import("./components/dashboards/assets/asset-list").then((m) => ({
    default: m.AssetList,
  }))
);
const AssetForm = lazy(() =>
  import("./components/dashboards/assets/asset-form").then((m) => ({
    default: m.AssetForm,
  }))
);
const AssetSubLocationList = lazy(() =>
  import("./components/dashboards/assets/asset-sub-location-list").then(
    (m) => ({
      default: m.AssetSubLocationList,
    })
  )
);
const AssetSubLocationForm = lazy(() =>
  import("./components/dashboards/assets/asset-sub-location-form").then(
    (m) => ({
      default: m.AssetSubLocationForm,
    })
  )
);

// Lazy-loaded route components - Monitoring Module
const MonitoringList = lazy(() =>
  import("./components/dashboards/monitoring/monitoring-list").then((m) => ({
    default: m.MonitoringList,
  }))
);
const AddMonitoringEvent = lazy(() =>
  import("./components/dashboards/monitoring/add-monitoring-event").then(
    (m) => ({
      default: m.AddMonitoringEvent,
    })
  )
);
const MonitoringLimits = lazy(() =>
  import("./components/dashboards/monitoring/monitoring-limits").then((m) => ({
    default: m.MonitoringLimits,
  }))
);
const MonitoringVariance = lazy(() =>
  import("./components/dashboards/monitoring/monitoring-variance").then(
    (m) => ({
      default: m.MonitoringVariance,
    })
  )
);
const MonitoringLocationForm = lazy(() =>
  import("./components/dashboards/monitoring/monitoring-location-form").then(
    (m) => ({ default: m.MonitoringLocationForm })
  )
);
const MonitoringLocationUploadPhotos = lazy(() =>
  import(
    "./components/dashboards/time-series/monitoring-location-upload-photos"
  ).then((m) => ({ default: m.MonitoringLocationUploadPhotos }))
);
const PhotoImportHistoryReview = lazy(() =>
  import(
    "./components/dashboards/time-series/photo-import-history-review"
  ).then((m) => ({ default: m.PhotoImportHistoryReview }))
);
const PhotoImportHistoryTraining = lazy(() =>
  import(
    "./components/dashboards/time-series/photo-import-history-training"
  ).then((m) => ({ default: m.PhotoImportHistoryTraining }))
);

// Lazy-loaded route components - Inspections Module
const InspectionList = lazy(() =>
  import("./components/dashboards/inspections/inspection-list").then((m) => ({
    default: m.InspectionList,
  }))
);

// Lazy-loaded route components - Commitments Module
const CommitmentList = lazy(() =>
  import("./components/dashboards/commitments/commitment-list").then((m) => ({
    default: m.default,
  }))
);
const CommitmentContainer = lazy(() =>
  import("./components/dashboards/commitments/commitment-container").then(
    (m) => ({
      default: m.default,
    })
  )
);

// Lazy-loaded route components - Investments Module
const InvestmentList = lazy(() =>
  import("./components/dashboards/investments/investment-list").then((m) => ({
    default: m.default,
  }))
);
const InvestmentContainer = lazy(() =>
  import("./components/dashboards/investments/investment-container").then(
    (m) => ({
      default: m.default,
    })
  )
);
const InvestmentSubmissionsList = lazy(() =>
  import(
    "./components/dashboards/investments/investment-submissions-list"
  ).then((m) => ({
    default: m.default,
  }))
);

const InspectionForm = lazy(() =>
  import("./components/dashboards/inspections/inspection-form").then((m) => ({
    default: m.InspectionForm,
  }))
);
const EquipmentList = lazy(() =>
  import("./components/dashboards/equipment/equipment-list").then((m) => ({
    default: m.EquipmentList,
  }))
);
const EquipmentDetails = lazy(() =>
  import("./components/dashboards/equipment/equipment-details").then((m) => ({
    default: m.EquipmentDetails,
  }))
);

// Lazy-loaded route components - Incidents Module
const IncidentsList = lazy(
  () => import("./components/dashboards/incidents/incidents-list")
);
const IncidentContainer = lazy(
  () => import("./components/dashboards/incidents/incident-container")
);

// Lazy-loaded route components - Actions Module
const ActionList = lazy(() =>
  import("./components/dashboards/actions/action-list").then((m) => ({
    default: m.ActionList,
  }))
);
const ActionForm = lazy(() =>
  import("./components/dashboards/actions/action-form").then((m) => ({
    default: m.ActionForm,
  }))
);

// Lazy-loaded route components - Permits Module
const PermitList = lazy(() =>
  import("./components/dashboards/permits/permit-list").then((m) => ({
    default: m.PermitList,
  }))
);
const PermitForm = lazy(() =>
  import("./components/dashboards/permits/permit-form").then((m) => ({
    default: m.PermitForm,
  }))
);

// Lazy-loaded route components - Contacts Module
const ContactList = lazy(() =>
  import("./components/dashboards/contacts/contact-list").then((m) => ({
    default: m.ContactList,
  }))
);
const ContactForm = lazy(() =>
  import("./components/dashboards/contacts/contact-form").then((m) => ({
    default: m.ContactForm,
  }))
);
const ContactGroupForm = lazy(() =>
  import("./components/dashboards/contacts/contact-group-form").then((m) => ({
    default: m.ContactGroupForm,
  }))
);

// Lazy-loaded route components - Training Module
const TrainingList = lazy(() =>
  import("./components/dashboards/training/training-list").then((m) => ({
    default: m.TrainingList,
  }))
);
const TrainingCourseForm = lazy(() =>
  import("./components/dashboards/training/training-course-form").then((m) => ({
    default: m.TrainingCourseForm,
  }))
);
const TrainingAssignmentForm = lazy(() =>
  import("./components/dashboards/training/training-assignment-form").then(
    (m) => ({
      default: m.TrainingAssignmentForm,
    })
  )
);

// Lazy-loaded route components - Documents Module
const DocumentList = lazy(
  () => import("./components/dashboards/documents/document-list")
);
const DocumentForm = lazy(
  () => import("./components/dashboards/documents/document-form")
);
const StagedCommitmentsList = lazy(
  () => import("./components/dashboards/documents/staged-commitments-list")
);
const StagedCommitmentDetails = lazy(
  () => import("./components/dashboards/documents/staged-commitment-details")
);

// Lazy-loaded route components - KPIs Module
const KpiList = lazy(() =>
  import("./components/dashboards/kpis/kpi-list").then((m) => ({
    default: m.KpiList,
  }))
);
const KpiDetails = lazy(() =>
  import("./components/dashboards/kpis/kpi-details").then((m) => ({
    default: m.KpiDetails,
  }))
);

// Lazy-loaded route components - Settings Module
const Settings = lazy(() =>
  import("./components/dashboards/settings/Settings").then((m) => ({
    default: m.Settings,
  }))
);
const AssetSettings = lazy(() =>
  import("./components/dashboards/settings/AssetSettings/AssetSettings").then(
    (m) => ({ default: m.AssetSettings })
  )
);
const TagSettings = lazy(() =>
  import(
    "./components/dashboards/settings/ComplianceSettings/tag-settings"
  ).then((m) => ({ default: m.TagSettings }))
);
const EventSettings = lazy(() =>
  import("./components/dashboards/settings/EventSettings/EventSettings").then(
    (m) => ({ default: m.EventSettings })
  )
);
const ContactSettings = lazy(() =>
  import("./components/dashboards/settings/contacts/contact-settings").then(
    (m) => ({ default: m.ContactSettings })
  )
);
const MonitoringSettings = lazy(() =>
  import(
    "./components/dashboards/settings/monitoring/monitoring-settings"
  ).then((m) => ({ default: m.MonitoringSettings }))
);
const OrganizationSettings = lazy(() =>
  import("./components/dashboards/settings/organization-settings").then(
    (m) => ({
      default: m.OrganizationSettings,
    })
  )
);
const UserForm = lazy(() =>
  import("./components/dashboards/settings/user-form").then((m) => ({
    default: m.UserForm,
  }))
);
const NotificationSettings = lazy(() =>
  import(
    "./components/dashboards/settings/notifications/notification-settings"
  ).then((m) => ({ default: m.NotificationSettings }))
);
const ActionSettings = lazy(() =>
  import("./components/dashboards/settings/actions/actions-settings").then(
    (m) => ({ default: m.ActionSettings })
  )
);

const IncidentSettings = lazy(() =>
  import("./components/dashboards/settings/incidents/incident-settings").then(
    (m) => ({ default: m.IncidentSettings })
  )
);
const InspectionSettings = lazy(() =>
  import(
    "./components/dashboards/settings/inspections/inspection-settings"
  ).then((m) => ({ default: m.InspectionSettings }))
);
const TrainingSettings = lazy(() =>
  import("./components/dashboards/settings/training/training-settings").then(
    (m) => ({ default: m.TrainingSettings })
  )
);
const CommitmentSettings = lazy(() =>
  import(
    "./components/dashboards/settings/commitments/CommitmentSettings"
  ).then((m) => ({ default: m.CommitmentSettings }))
);
const InvestmentSettings = lazy(() =>
  import(
    "./components/dashboards/settings/investments/InvestmentSettings"
  ).then((m) => ({ default: m.InvestmentSettings }))
);
const InvestmentSubmissionFormBuilder = lazy(() =>
  import(
    "./components/dashboards/settings/investments/investment-submission-form-builder"
  ).then((m) => ({ default: m.InvestmentSubmissionFormBuilder }))
);
const InvestmentSubmissionReviewSettings = lazy(() =>
  import(
    "./components/dashboards/settings/investments/investment-submission-review-settings"
  ).then((m) => ({ default: m.InvestmentSubmissionReviewSettings }))
);
const PermitSettings = lazy(() =>
  import("./components/dashboards/settings/permits/permit-settings").then(
    (m) => ({ default: m.PermitSettings })
  )
);

const CustomerPortalSettings = lazy(() =>
  import(
    "./components/dashboards/settings/customer-portal/customer-portal-settings"
  ).then((m) => ({ default: m.CustomerPortalSettings }))
);
const CustomerPortalHistory = lazy(() =>
  import(
    "./components/dashboards/settings/customer-portal/customer-portal-history"
  ).then((m) => ({ default: m.CustomerPortalHistory }))
);
const OrganizationPortalSettings = lazy(() =>
  import(
    "./components/dashboards/settings/organization-portal/organization-portal-settings"
  ).then((m) => ({ default: m.OrganizationPortalSettings }))
);
const OrganizationPortalSubmissions = lazy(() =>
  import(
    "./components/dashboards/settings/organization-portal/organization-portal-submissions"
  ).then((m) => ({ default: m.OrganizationPortalSubmissions }))
);
const FormBuilder = lazy(() =>
  import("./components/dashboards/settings/custom-fields/form-builder").then(
    (m) => ({ default: m.FormBuilder })
  )
);
const InspectionFormBuilder = lazy(() =>
  import(
    "./components/dashboards/settings/custom-fields/inspection-form-builder"
  ).then((m) => ({ default: m.InspectionFormBuilder }))
);
const TopicSettings = lazy(() =>
  import("./components/dashboards/settings/topics/topic-settings").then(
    (m) => ({
      default: m.TopicSettings,
    })
  )
);
const EquipmentSettings = lazy(() =>
  import("./components/dashboards/settings/equipments/equipment-settings").then(
    (m) => ({ default: m.EquipmentSettings })
  )
);
const ExportsSettings = lazy(() =>
  import("./components/dashboards/settings/exports/exports-settings").then(
    (m) => ({ default: m.ExportsSettings })
  )
);
const ComplianceList = lazy(
  () => import("./components/dashboards/settings/compliance/compliance-list")
);

// Lazy-loaded route components - Dashboard & Map
const UserDashboard = lazy(() =>
  import("./components/dashboards/dashboards/user-dashboard").then((m) => ({
    default: m.UserDashboard,
  }))
);
const DashboardMap = lazy(() =>
  import("./components/dashboards/map/dashboard-map").then((m) => ({
    default: m.DashboardMap,
  }))
);

// Lazy-loaded route components - Imports Module
const Imports = lazy(() =>
  import("./components/dashboards/imports/imports").then((m) => ({
    default: m.Imports,
  }))
);
const ImportHistory = lazy(() =>
  import("./components/dashboards/imports/import-history").then((m) => ({
    default: m.ImportHistory,
  }))
);
const ImportSpecifications = lazy(() =>
  import("./components/dashboards/imports/import-specifications").then((m) => ({
    default: m.ImportSpecifications,
  }))
);

// Lazy-loaded route components - Admin Module
const OrganizationList = lazy(() =>
  import("./components/admin/organization-list").then((m) => ({
    default: m.OrganizationList,
  }))
);
const CustomerList = lazy(() =>
  import("./components/admin/customer-list").then((m) => ({
    default: m.CustomerList,
  }))
);
const ChatMessageList = lazy(() =>
  import("./components/admin/chat-message-list").then((m) => ({
    default: m.ChatMessageList,
  }))
);

// Lazy-loaded route components - Portal & Microsites
const OrganizationPortalPage = lazy(() =>
  import("./components/organization-portal/OrganizationPortalPage").then(
    (m) => ({
      default: m.OrganizationPortalPage,
    })
  )
);
const InvestmentSubmissionPortalPage = lazy(() =>
  import(
    "./components/investment-submission-portal/InvestmentSubmissionPortalPage"
  ).then((m) => ({
    default: m.InvestmentSubmissionPortalPage,
  }))
);

const WylooMicrositeHome = lazy(() =>
  import("./components/microsites/wyloo/wyloo-microsite-home").then((m) => ({
    default: m.WylooMicrositeHome,
  }))
);
const WylooMicrositeWater = lazy(() =>
  import("./components/microsites/wyloo/wyloo-microsite-water").then((m) => ({
    default: m.WylooMicrositeWater,
  }))
);
const WylooMicrositeAir = lazy(() =>
  import("./components/microsites/wyloo/wyloo-microsite-air").then((m) => ({
    default: m.WylooMicrositeAir,
  }))
);
const WylooMicrositeWildlife = lazy(() =>
  import("./components/microsites/wyloo/wyloo-microsite-wildlife").then(
    (m) => ({
      default: m.WylooMicrositeWildlife,
    })
  )
);
const WylooLocationDetails = lazy(() =>
  import("./components/microsites/wyloo/wyloo-location-details").then((m) => ({
    default: m.WylooLocationDetails,
  }))
);

// Lazy-loaded route components - Misc
const AuditLogList = lazy(() =>
  import("./components/dashboards/audit-log/audit-log-list").then((m) => ({
    default: m.AuditLogList,
  }))
);
const About = lazy(() =>
  import("./components/dashboards/about/about").then((m) => ({
    default: m.About,
  }))
);
const AiPlayground = lazy(
  () => import("./components/dashboards/ai/ai-playground")
);
const RubyPlayground = lazy(
  () => import("./components/dashboards/ai/ruby-playground")
);
const OrganizationSelection = lazy(() =>
  import("./components/organization-selection").then((m) => ({
    default: m.OrganizationSelection,
  }))
);
const RegisterAccount = lazy(() =>
  import("./components/login/register-account").then((m) => ({
    default: m.RegisterAccount,
  }))
);
const StripeCheckout = lazy(() => import("./components/stripe"));
const StripeCheckoutSuccess = lazy(() => import("./components/stripe/success"));

// Lazy-loaded route components - Auth & Core Pages
const LoginContainer = lazy(() => import("./containers/LoginContainer"));
const AccountContainer = lazy(() => import("./containers/AccountContainer"));
const PrivacyPolicy = lazy(() =>
  import("./components/privacy").then((m) => ({ default: m.PrivacyPolicy }))
);

// Loading component for Suspense fallback
const RouteLoading = () => (
  <Box
    display="flex"
    justifyContent="center"
    alignItems="center"
    minHeight="400px"
    width="100%"
  >
    <CircularProgress />
  </Box>
);

export default function AppRoute(props) {
  return (
    <Suspense fallback={<RouteLoading />}>
      <Routes>
        <Route path="/" element={<LoginContainer />} />
        <Route path="/two-factor" element={<LoginContainer />} />

        <Route path="/privacy" element={<PrivacyPolicy />} />

        <Route path="/p/:slug" element={<OrganizationPortalPage />} />
        <Route path="/d/:slug" element={<InvestmentSubmissionPortalPage />} />
        <Route path="/wyloo" element={<WylooMicrositeHome />} />
        <Route path="/ruby-ai" element={<RubyPlayground />} />
        <Route path="/wyloo/water" element={<WylooMicrositeWater />} />
        <Route path="/wyloo/air" element={<WylooMicrositeAir />} />
        <Route path="/wyloo/wildlife" element={<WylooMicrositeWildlife />} />
        <Route path="/organizations" element={<OrganizationSelection />} />
        <Route
          path="/wyloo/monitoring-locations/:id"
          element={
            <RequirePortalAuth
              roles={[UserRoles.PortalAdmin, UserRoles.PortalUser]}
            >
              <WylooLocationDetails />
            </RequirePortalAuth>
          }
        />

        <Route
          path="/account"
          element={
            <RequireAuth>
              <AccountContainer />
            </RequireAuth>
          }
        />
        <Route path="/authorize" element={<LoginContainer />} />
        <Route
          path="/dashboard"
          element={
            <RequireAuth>
              <UserDashboard />
            </RequireAuth>
          }
        />

        <Route
          path="/map"
          element={
            <RequireAuth>
              <DashboardMap />
            </RequireAuth>
          }
        />
        <Route
          path="/assets"
          element={
            <RequireAuth roles={[UserRoles.Admin]}>
              <AssetList />
            </RequireAuth>
          }
        />
        <Route
          path="/assets/:assetId"
          element={
            <RequireAuth roles={[UserRoles.Admin]}>
              <AssetForm />
            </RequireAuth>
          }
        />
        <Route
          path="/add-asset"
          element={
            <RequireAuth roles={[UserRoles.Admin]}>
              <AssetForm />
            </RequireAuth>
          }
        />

        <Route
          path="/events"
          element={
            <RequireAuth
              roles={[
                UserRoles.EventsAdministrator,
                UserRoles.EventsEditor,
                UserRoles.EventsContributor,
                UserRoles.EventsViewer,
              ]}
            >
              <EventList />
            </RequireAuth>
          }
        />
        <Route
          path="/add-event"
          element={
            <RequireAuth
              roles={[UserRoles.EventsAdministrator, UserRoles.EventsEditor]}
            >
              <EventForm entityId={0} />
            </RequireAuth>
          }
        />
        <Route path="/offline/add-event" element={<EventForm entityId={0} />} />
        <Route
          path="/events/:eventId"
          element={
            <RequireAuth
              roles={[
                UserRoles.EventsAdministrator,
                UserRoles.EventsEditor,
                UserRoles.EventsContributor,
                UserRoles.EventsViewer,
              ]}
            >
              <EventDetailsContainer />
            </RequireAuth>
          }
        />
        <Route
          path="/events/:eventId/add-commitment"
          element={
            <RequireAuth when={canAccessCommitmentsModule}>
              <CommitmentContainer />
            </RequireAuth>
          }
        />
        <Route
          path="/photo-import-history/:photoImportHistoryId"
          element={
            <RequireAuth roles={[UserRoles.TimeSeriesUser]}>
              <PhotoImportHistoryReview />
            </RequireAuth>
          }
        />
        <Route
          path="/photo-import-history/:photoImportHistoryId/training"
          element={
            <RequireAuth roles={[UserRoles.TimeSeriesUser]}>
              <PhotoImportHistoryTraining />
            </RequireAuth>
          }
        />

        <Route
          path="/incidents"
          element={
            <RequireAuth roles={[UserRoles.IncidentsUser]}>
              <IncidentsList />
            </RequireAuth>
          }
        />
        <Route
          path="/actions"
          element={
            <RequireAuth>
              <ActionList />
            </RequireAuth>
          }
        />
        <Route
          path="/add-action"
          element={
            <RequireAuth>
              <ActionForm />
            </RequireAuth>
          }
        />
        <Route
          path="/actions/:id"
          element={
            <RequireAuth>
              <ActionForm />
            </RequireAuth>
          }
        />
        <Route
          path="/events/:eventId/add-action"
          element={
            <RequireAuth
              roles={[
                UserRoles.EventsAdministrator,
                UserRoles.EventsEditor,
                UserRoles.EventsContributor,
              ]}
            >
              <ActionForm />
            </RequireAuth>
          }
        />
        <Route
          path="/events/:eventId/actions/:id"
          element={
            <RequireAuth
              roles={[
                UserRoles.EventsAdministrator,
                UserRoles.EventsEditor,
                UserRoles.EventsContributor,
                UserRoles.EventsViewer,
              ]}
            >
              <ActionForm />
            </RequireAuth>
          }
        />
        <Route
          path="/submit-incident"
          element={
            <RequireAuth roles={[UserRoles.IncidentsUser]}>
              <IncidentContainer />
            </RequireAuth>
          }
        />
        <Route
          path="/incidents/:id"
          element={
            <RequireAuth roles={[UserRoles.IncidentsUser]}>
              <IncidentContainer />
            </RequireAuth>
          }
        />
        <Route
          path="/incidents/:incidentId/add-action"
          element={
            <RequireAuth roles={[UserRoles.IncidentsUser]}>
              <ActionForm />
            </RequireAuth>
          }
        />
        <Route
          path="/incidents/:incidentId/actions/:id"
          element={
            <RequireAuth roles={[UserRoles.IncidentsUser]}>
              <ActionForm />
            </RequireAuth>
          }
        />
        <Route
          path="/add-monitoring-event"
          element={
            <RequireAuth
              roles={[
                UserRoles.MonitoringAdministrator,
                UserRoles.MonitoringEditor,
              ]}
            >
              <AddMonitoringEvent />
            </RequireAuth>
          }
        />
        <Route
          path="/monitoring-locations/:monLocId/limits"
          element={
            <RequireAuth
              roles={[
                UserRoles.MonitoringAdministrator,
                UserRoles.MonitoringEditor,
                UserRoles.MonitoringViewer,
              ]}
            >
              <MonitoringLimits />
            </RequireAuth>
          }
        />
        <Route
          path="settings/monitoring-limits/:lookupId"
          element={
            <RequireAuth
              roles={[
                UserRoles.MonitoringAdministrator,
                UserRoles.MonitoringEditor,
                UserRoles.MonitoringViewer,
              ]}
            >
              <MonitoringLimits />
            </RequireAuth>
          }
        />
        <Route
          path="/monitoring-locations/:monLocId/variance"
          element={
            <RequireAuth
              roles={[
                UserRoles.MonitoringAdministrator,
                UserRoles.MonitoringEditor,
                UserRoles.MonitoringViewer,
              ]}
            >
              <MonitoringVariance />
            </RequireAuth>
          }
        />
        <Route
          path="/monitoring/:eventId"
          element={
            <RequireAuth
              roles={[
                UserRoles.MonitoringAdministrator,
                UserRoles.MonitoringEditor,
                UserRoles.MonitoringViewer,
              ]}
            >
              <AddMonitoringEvent />
            </RequireAuth>
          }
        />
        <Route
          path="/monitoring"
          element={
            <RequireAuth
              roles={[
                UserRoles.MonitoringAdministrator,
                UserRoles.MonitoringEditor,
                UserRoles.MonitoringViewer,
              ]}
            >
              <MonitoringList />
            </RequireAuth>
          }
        />
        <Route
          path="/monitoring-locations/:monLocId"
          element={
            <RequireAuth
              roles={[
                UserRoles.MonitoringAdministrator,
                UserRoles.MonitoringEditor,
                UserRoles.MonitoringViewer,
              ]}
            >
              <MonitoringLocationForm />
            </RequireAuth>
          }
        />
        <Route
          path="/add-monitoring-location"
          element={
            <RequireAuth
              roles={[
                UserRoles.MonitoringAdministrator,
                UserRoles.MonitoringEditor,
              ]}
            >
              <MonitoringLocationForm />
            </RequireAuth>
          }
        />
        <Route
          path="/inspections"
          element={
            <RequireAuth roles={[UserRoles.InspectionsUser]}>
              <InspectionList />
            </RequireAuth>
          }
        />
        <Route
          path="/commitments"
          element={
            <RequireAuth when={canAccessCommitmentsModule}>
              <CommitmentList />
            </RequireAuth>
          }
        />
        <Route
          path="/add-commitment"
          element={
            <RequireAuth when={canAccessCommitmentsModule}>
              <CommitmentContainer />
            </RequireAuth>
          }
        />
        <Route
          path="/commitments/:commitmentId/add-linked-commitment"
          element={
            <RequireAuth when={canAccessCommitmentsModule}>
              <CommitmentContainer />
            </RequireAuth>
          }
        />
        <Route
          path="/commitments/:commitmentId"
          element={
            <RequireAuth when={canAccessCommitmentsModule}>
              <CommitmentContainer />
            </RequireAuth>
          }
        />
        <Route
          path="/investments"
          element={
            <RequireAuth roles={[UserRoles.Admin, UserRoles.SuperAdmin]}>
              <InvestmentList />
            </RequireAuth>
          }
        />
        <Route
          path="/add-investment"
          element={
            <RequireAuth roles={[UserRoles.Admin, UserRoles.SuperAdmin]}>
              <InvestmentContainer />
            </RequireAuth>
          }
        />
        <Route
          path="/investments/:investmentId"
          element={
            <RequireAuth roles={[UserRoles.Admin, UserRoles.SuperAdmin]}>
              <InvestmentContainer />
            </RequireAuth>
          }
        />
        <Route
          path="/investment-submissions"
          element={
            <RequireAuth roles={[UserRoles.Admin, UserRoles.SuperAdmin]}>
              <InvestmentSubmissionsList />
            </RequireAuth>
          }
        />
        <Route
          path="/equipment"
          element={
            <RequireAuth roles={[UserRoles.InspectionsUser]}>
              <EquipmentList />
            </RequireAuth>
          }
        />
        <Route
          path="/equipment/:id"
          element={
            <RequireAuth roles={[UserRoles.InspectionsUser]}>
              <EquipmentDetails />
            </RequireAuth>
          }
        />
        <Route
          path="/add-equipment"
          element={
            <RequireAuth roles={[UserRoles.InspectionsUser]}>
              <EquipmentDetails />
            </RequireAuth>
          }
        />
        <Route
          path="/add-inspection"
          element={
            <RequireAuth roles={[UserRoles.InspectionsUser]}>
              <InspectionForm />
            </RequireAuth>
          }
        />
        <Route path="/offline/add-inspection" element={<InspectionForm />} />
        <Route
          path="/inspections/:inspectionId"
          element={
            <RequireAuth roles={[UserRoles.InspectionsUser]}>
              <InspectionForm />
            </RequireAuth>
          }
        />

        <Route
          path="/permits"
          element={
            <RequireAuth
              roles={[UserRoles.Admin, UserRoles.EventsAdministrator]}
            >
              <PermitList />
            </RequireAuth>
          }
        />
        <Route
          path="/permits/:id"
          element={
            <RequireAuth
              roles={[UserRoles.Admin, UserRoles.EventsAdministrator]}
            >
              <PermitForm />
            </RequireAuth>
          }
        />
        <Route
          path="/permits/:id/staged-commitments"
          element={
            <RequireAuth
              roles={[UserRoles.Admin, UserRoles.EventsAdministrator]}
            >
              <StagedCommitmentsList entityType="permit" />
            </RequireAuth>
          }
        />
        <Route
          path="/permits/:permitId/staged-commitments/:stagedCommitmentId"
          element={
            <RequireAuth
              roles={[UserRoles.Admin, UserRoles.EventsAdministrator]}
            >
              <StagedCommitmentDetails
                isExistingStagedCommitment={true}
                entityType="permit"
              />
            </RequireAuth>
          }
        />
        <Route
          path="/add-permit"
          element={
            <RequireAuth
              roles={[UserRoles.Admin, UserRoles.EventsAdministrator]}
            >
              <PermitForm />
            </RequireAuth>
          }
        />
        <Route
          path="/monitoring-location-upload-photos"
          element={
            <RequireAuth roles={[UserRoles.TimeSeriesUser]}>
              <MonitoringLocationUploadPhotos />
            </RequireAuth>
          }
        />
        <Route
          path="/settings"
          element={
            <RequireAuth>
              <Settings />
            </RequireAuth>
          }
        />
        <Route
          path="/settings/incidents"
          element={
            <RequireAuth roles={[UserRoles.IncidentsUser, UserRoles.Admin]}>
              <IncidentSettings />
            </RequireAuth>
          }
        />
        <Route
          path="/settings/portal"
          element={
            <RequireAuth roles={[UserRoles.Admin]}>
              <CustomerPortalSettings />
            </RequireAuth>
          }
        />
        <Route
          path="/settings/portal/history"
          element={
            <RequireAuth roles={[UserRoles.Admin]}>
              <CustomerPortalHistory />
            </RequireAuth>
          }
        />
        <Route
          path="/settings/organization-portal"
          element={
            <RequireAuth roles={[UserRoles.Admin]}>
              <OrganizationPortalSettings />
            </RequireAuth>
          }
        />
        <Route
          path="/portal-submissions"
          element={
            <RequireAuth>
              <OrganizationPortalSubmissions />
            </RequireAuth>
          }
        />
        <Route
          path="/settings/events"
          element={
            <RequireAuth
              roles={[UserRoles.Admin, UserRoles.EventsAdministrator]}
            >
              <EventSettings />
            </RequireAuth>
          }
        />
        <Route
          path="/settings/inspections"
          element={
            <RequireAuth roles={[UserRoles.Admin]}>
              <InspectionSettings />
            </RequireAuth>
          }
        />
        <Route
          path="/settings/training"
          element={
            <RequireAuth>
              <TrainingSettings />
            </RequireAuth>
          }
        />
        <Route
          path="/settings/commitments"
          element={
            <RequireAuth
              roles={[UserRoles.Admin, UserRoles.EventsAdministrator]}
            >
              <CommitmentSettings />
            </RequireAuth>
          }
        />
        <Route
          path="/settings/investments"
          element={
            <RequireAuth
              roles={[UserRoles.Admin, UserRoles.EventsAdministrator]}
            >
              <InvestmentSettings />
            </RequireAuth>
          }
        />
        <Route
          path="/settings/investment-submission-form"
          element={
            <RequireAuth roles={[UserRoles.Admin, UserRoles.SuperAdmin]}>
              <InvestmentSubmissionFormBuilder />
            </RequireAuth>
          }
        />
        <Route
          path="/settings/investment-submission-review-statuses"
          element={
            <RequireAuth roles={[UserRoles.Admin, UserRoles.SuperAdmin]}>
              <InvestmentSubmissionReviewSettings />
            </RequireAuth>
          }
        />
        <Route
          path="/settings/incidents/form-builder"
          element={
            <RequireAuth
              roles={[UserRoles.IncidentsUser, UserRoles.InternalUser]}
            >
              <FormBuilder />
            </RequireAuth>
          }
        />
        <Route
          path="/settings/incidents/form-builder/:id"
          element={
            <RequireAuth
              roles={[UserRoles.IncidentsUser, UserRoles.InternalUser]}
            >
              <FormBuilder />
            </RequireAuth>
          }
        />
        <Route
          path="/settings/inspections/form-builder"
          element={
            <RequireAuth
              roles={[
                UserRoles.InspectionsUser,
                UserRoles.InternalUser,
                UserRoles.Admin,
              ]}
            >
              <InspectionFormBuilder />
            </RequireAuth>
          }
        />
        <Route
          path="/settings/inspections/form-builder/:id"
          element={
            <RequireAuth
              roles={[
                UserRoles.InspectionsUser,
                UserRoles.InternalUser,
                UserRoles.Admin,
              ]}
            >
              <InspectionFormBuilder />
            </RequireAuth>
          }
        />

        <Route
          path="/inspections/:inspectionEventId/add-action"
          element={
            <RequireAuth roles={[UserRoles.InspectionsUser]}>
              <ActionForm />
            </RequireAuth>
          }
        />
        <Route
          path="/inspections/:inspectionEventId/actions/:id"
          element={
            <RequireAuth roles={[UserRoles.InspectionsUser]}>
              <ActionForm />
            </RequireAuth>
          }
        />

        <Route
          path="/settings/assets"
          element={
            <RequireAuth roles={[UserRoles.Admin]}>
              <AssetSettings />
            </RequireAuth>
          }
        />
        <Route
          path="/settings/tags"
          element={
            <RequireAuth
              roles={[
                UserRoles.Admin,
                UserRoles.EventsAdministrator,
                UserRoles.EventsEditor,
              ]}
            >
              <TagSettings />
            </RequireAuth>
          }
        />
        <Route
          path="/settings/contacts"
          element={
            <RequireAuth
              roles={[UserRoles.Admin, UserRoles.EventsAdministrator]}
            >
              <ContactSettings />
            </RequireAuth>
          }
        />
        <Route
          path="/settings/monitoring"
          element={
            <RequireAuth
              roles={[
                UserRoles.MonitoringAdministrator,
                UserRoles.MonitoringEditor,
                UserRoles.Admin,
              ]}
            >
              <MonitoringSettings />
            </RequireAuth>
          }
        />
        <Route
          path="/settings/actions"
          element={
            <RequireAuth
              roles={[UserRoles.Admin, UserRoles.EventsAdministrator]}
            >
              <ActionSettings />
            </RequireAuth>
          }
        />

        <Route
          path="/settings/permits"
          element={
            <RequireAuth
              roles={[UserRoles.Admin, UserRoles.EventsAdministrator]}
            >
              <PermitSettings />
            </RequireAuth>
          }
        />
        <Route
          path="/contacts"
          element={
            <RequireAuth>
              <ContactList />
            </RequireAuth>
          }
        />
        <Route
          path="/add-contact"
          element={
            <RequireAuth
              roles={[
                UserRoles.Admin,
                UserRoles.EventsAdministrator,
                UserRoles.EventsEditor,
              ]}
            >
              <ContactForm />
            </RequireAuth>
          }
        />
        <Route
          path="/contacts/:contactId"
          element={
            <RequireAuth
              roles={[
                UserRoles.Admin,
                UserRoles.EventsAdministrator,
                UserRoles.EventsEditor,
              ]}
            >
              <ContactForm />
            </RequireAuth>
          }
        />
        <Route
          path="/add-contact-group"
          element={
            <RequireAuth
              roles={[
                UserRoles.Admin,
                UserRoles.EventsAdministrator,
                UserRoles.EventsEditor,
              ]}
            >
              <ContactGroupForm />
            </RequireAuth>
          }
        />
        <Route
          path="/contact-groups/:contactGroupId"
          element={
            <RequireAuth
              roles={[
                UserRoles.Admin,
                UserRoles.EventsAdministrator,
                UserRoles.EventsEditor,
              ]}
            >
              <ContactGroupForm />
            </RequireAuth>
          }
        />
        <Route
          path="/training"
          element={
            <RequireAuth>
              <TrainingList />
            </RequireAuth>
          }
        />
        <Route
          path="/add-training-course"
          element={
            <RequireAuth>
              <TrainingCourseForm />
            </RequireAuth>
          }
        />
        <Route
          path="/training/courses/:courseId"
          element={
            <RequireAuth>
              <TrainingCourseForm />
            </RequireAuth>
          }
        />
        <Route
          path="/training-assignments/:id"
          element={
            <RequireAuth>
              <TrainingAssignmentForm />
            </RequireAuth>
          }
        />
        <Route
          path="/settings/organization"
          element={
            <RequireAuth roles={[UserRoles.Admin, UserRoles.InternalUser]}>
              <OrganizationSettings />
            </RequireAuth>
          }
        />
        <Route
          path="/settings/users/:id"
          element={
            <RequireAuth roles={[UserRoles.Admin, UserRoles.InternalUser]}>
              <UserForm />
            </RequireAuth>
          }
        />
        <Route
          path="/settings/notifications"
          element={
            <RequireAuth>
              <NotificationSettings />
            </RequireAuth>
          }
        />
        <Route
          path="/settings/exports"
          element={
            <RequireAuth>
              <ExportsSettings />
            </RequireAuth>
          }
        />
        <Route
          path="/audit-log"
          element={
            <RequireAuth roles={[UserRoles.Admin]}>
              <AuditLogList />
            </RequireAuth>
          }
        />

        <Route
          path="/admin/organizations"
          element={
            <RequireAuth roles={[UserRoles.SuperAdmin]}>
              <OrganizationList />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/chat-messages"
          element={
            <RequireAuth roles={[UserRoles.SuperAdmin]}>
              <ChatMessageList />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/customers"
          element={
            <RequireAuth roles={[UserRoles.SuperAdmin]}>
              <CustomerList />
            </RequireAuth>
          }
        />
        <Route
          path="/kpis"
          element={
            <RequireAuth>
              <KpiList />
            </RequireAuth>
          }
        />
        <Route
          path="/kpis/:id"
          element={
            <RequireAuth>
              <KpiDetails />
            </RequireAuth>
          }
        />
        <Route
          path="/about"
          element={
            <RequireAuth>
              <About />
            </RequireAuth>
          }
        />
        <Route
          path="/imports"
          element={
            <RequireAuth
              roles={[
                UserRoles.Admin,
                UserRoles.EventsAdministrator,
                UserRoles.MonitoringAdministrator,
              ]}
            >
              <Imports />
            </RequireAuth>
          }
        />
        <Route
          path="/import-history"
          element={
            <RequireAuth
              roles={[
                UserRoles.Admin,
                UserRoles.EventsAdministrator,
                UserRoles.MonitoringAdministrator,
              ]}
            >
              <ImportHistory />
            </RequireAuth>
          }
        />
        <Route
          path="/import-configurations"
          element={
            <RequireAuth
              roles={[
                UserRoles.Admin,
                UserRoles.EventsAdministrator,
                UserRoles.MonitoringAdministrator,
              ]}
            >
              <ImportSpecifications />
            </RequireAuth>
          }
        />
        <Route
          path="/ai"
          element={
            <RequireAuth roles={[UserRoles.Admin]}>
              <AiPlayground />
            </RequireAuth>
          }
        />
        <Route
          path="/compliance"
          element={
            <RequireAuth roles={[UserRoles.Admin]}>
              <ComplianceList />
            </RequireAuth>
          }
        />
        <Route
          path="/documents"
          element={
            <RequireAuth>
              <DocumentList />
            </RequireAuth>
          }
        />
        <Route
          path="/upload-documents"
          element={
            <RequireAuth>
              <DocumentForm />
            </RequireAuth>
          }
        />
        <Route
          path="/documents/:id"
          element={
            <RequireAuth>
              <DocumentForm />
            </RequireAuth>
          }
        />
        <Route
          path="/documents/:id/staged-commitments"
          element={
            <RequireAuth>
              <StagedCommitmentsList />
            </RequireAuth>
          }
        />
        <Route
          path="/documents/:documentId/staged-commitments/:stagedCommitmentId"
          element={
            <RequireAuth roles={[UserRoles.Admin]}>
              <StagedCommitmentDetails isExistingStagedCommitment={true} />
            </RequireAuth>
          }
        />
        <Route
          path="/assets/:assetId/asset-sub-locations"
          element={
            <RequireAuth roles={[UserRoles.Admin]}>
              <AssetSubLocationList />
            </RequireAuth>
          }
        />
        <Route
          path="/assets/:assetId/add-asset-sub-location"
          element={
            <RequireAuth roles={[UserRoles.Admin]}>
              <AssetSubLocationForm />
            </RequireAuth>
          }
        />

        <Route
          path="/asset-sub-location/:assetSubLocationId"
          element={
            <RequireAuth roles={[UserRoles.Admin]}>
              <AssetSubLocationForm />
            </RequireAuth>
          }
        />

        <Route
          path="/settings/topics"
          element={
            <RequireAuth
              roles={[
                UserRoles.Admin,
                UserRoles.EventsAdministrator,
                UserRoles.EventsEditor,
              ]}
            >
              <TopicSettings />
            </RequireAuth>
          }
        />

        <Route
          path="/settings/equipments"
          element={
            <RequireAuth roles={[UserRoles.Admin]}>
              <EquipmentSettings />
            </RequireAuth>
          }
        />

        <Route path="/register/:guid" element={<RegisterAccount />} />

        <Route path="/billing" element={<StripeCheckout />} />
        <Route path="/billing/success" element={<StripeCheckoutSuccess />} />
        <Route path="*" element={<PageNotFound />} />
      </Routes>
    </Suspense>
  );
}
